import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Pencil, Sparkles, Square, Save, X, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, streamSse } from '@/lib/api';
import { toast } from '@/components/ui/toast';

interface Props {
  episodeId: string;
  initial: string;
  onSaved: () => void;
  locked?: boolean;
}

type RewriteEvent =
  | { event: 'phase'; phase: string }
  | { event: 'delta'; text: string }
  | { event: 'done'; storyline: string; updatedAt: string }
  | { event: 'error'; message: string };

function lengthTone(n: number): string {
  if (n === 0) return 'text-neutral-400';
  if (n < 400) return 'text-warning';
  if (n <= 1200) return 'text-success';
  return 'text-neutral-500';
}

export function StorylineQuickEditor({ episodeId, initial, onSaved, locked = false }: Props) {
  const [mode, setMode] = useState<'view' | 'manual' | 'ai'>('view');
  const [draft, setDraft] = useState(initial);
  const [prompt, setPrompt] = useState('');
  const [streaming, setStreaming] = useState(false);
  const ctrlRef = useRef<AbortController | null>(null);
  const promptRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    if (streaming && textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, [draft, streaming]);

  useEffect(() => {
    if (mode === 'view') setDraft(initial);
  }, [initial, mode]);

  const enterManual = () => {
    setDraft(initial);
    setMode('manual');
  };

  const enterAi = () => {
    setDraft(initial);
    setPrompt('');
    setMode('ai');
    setTimeout(() => promptRef.current?.focus(), 50);
  };

  const exitEdit = () => {
    if (streaming) {
      ctrlRef.current?.abort();
      ctrlRef.current = null;
      setStreaming(false);
    }
    setDraft(initial);
    setPrompt('');
    setMode('view');
  };

  const saveMut = useMutation({
    mutationFn: async () =>
      api
        .patch(`writer/episodes/${episodeId}/storyline`, {
          json: { storyline: draft.trim() },
        })
        .json<{ storyline: string; updatedAt: string }>(),
    onSuccess: () => {
      toast.success('已保存剧情大纲');
      onSaved();
      setMode('view');
    },
    onError: (err: Error) => toast.error('保存失败', err.message),
  });

  const startGenerate = () => {
    const p = prompt.trim();
    if (!p) {
      toast.warning('请输入改写要求', '例如:加重女主反转 / 改用甜虐基调');
      return;
    }
    const original = draft;
    let gotDelta = false;
    setStreaming(true);
    const ctrl = streamSse<RewriteEvent>(
      `writer/episodes/${episodeId}/storyline/rewrite`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          episodeId,
          current: initial,
          prompt: p,
          targetChars: 1000,
        }),
      },
      (ev) => {
        if (ev.event === 'delta') {
          if (!gotDelta) {
            gotDelta = true;
            setDraft(ev.text);
          } else {
            setDraft((d) => d + ev.text);
          }
        } else if (ev.event === 'done') {
          setStreaming(false);
          ctrlRef.current = null;
          if (!gotDelta && !ev.storyline) {
            setDraft(original);
            toast.warning('AI 未返回内容', '已恢复原文,换个提示词再试');
            return;
          }
          if (ev.storyline) setDraft(ev.storyline);
          toast.success('AI 改写完成', '确认后点「保存」入库');
        } else if (ev.event === 'error') {
          setStreaming(false);
          ctrlRef.current = null;
          if (!gotDelta) setDraft(original);
          toast.error('改写失败', ev.message);
        }
      },
      (err) => {
        setStreaming(false);
        ctrlRef.current = null;
        const name = err instanceof Error ? err.name : '';
        if (name === 'AbortError') return;
        if (!gotDelta) setDraft(original);
        toast.error('改写失败', err instanceof Error ? err.message : String(err));
      },
    );
    ctrlRef.current = ctrl;
  };

  const abortGenerate = () => {
    ctrlRef.current?.abort();
    ctrlRef.current = null;
    setStreaming(false);
    toast.info('已停止生成');
  };

  // ───────── view 态 ─────────
  if (mode === 'view') {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-outline">剧情大纲</div>
          {!locked && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={enterManual}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-neutral-600 transition hover:bg-surface-variant hover:text-brand-600"
              >
                <Pencil className="h-3 w-3" /> 编辑
              </button>
              <button
                type="button"
                onClick={enterAi}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-brand-600 transition hover:bg-brand-50"
              >
                <Wand2 className="h-3 w-3" /> AI 改写
              </button>
            </div>
          )}
        </div>
        {initial ? (
          <p className="max-h-[40vh] overflow-y-auto whitespace-pre-line rounded-lg bg-surface-variant/40 p-3 leading-relaxed text-neutral-700">
            {initial}
          </p>
        ) : (
          <p className="rounded-lg border border-dashed border-outline-variant/40 px-3 py-3 text-xs text-neutral-500">
            {locked
              ? '本项目无剧情大纲 · 已锁定不可补写'
              : '立项阶段尚未生成快看,点击「编辑」手动写,或「AI 改写」让 AI 基于 IP 设定生成。'}
          </p>
        )}
      </div>
    );
  }

  // ───────── 手动编辑态 · 全屏 ─────────
  if (mode === 'manual') {
    return (
      <>
        <div
          className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{marginTop: '0px'}}
          onClick={exitEdit}
        />
        <div
          className="fixed inset-2 z-50 flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-2xl sm:inset-6 sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-outline-variant/30 pb-3">
            <div>
              <h3 className="text-base font-semibold text-neutral-800">剧情大纲 · 全屏编辑</h3>
              <span className={`text-xs ${lengthTone(draft.length)}`}>{draft.length} 字</span>
            </div>
            <button
              type="button"
              onClick={exitEdit}
              disabled={saveMut.isPending}
              className="rounded p-1 text-neutral-400 hover:text-neutral-600"
              title="退出全屏 (未保存将丢失)"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="沿用 IP 设定（主角名 / 核心冲突 / 开场钩子），按短剧节拍写剧情大纲。"
            className="flex-1 resize-none rounded-xl border border-outline-variant/30 bg-surface-variant/20 px-4 py-3 text-sm leading-relaxed text-neutral-800 placeholder:text-neutral-400 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-200"
            autoFocus
          />

          <div className="flex items-center justify-between border-t border-outline-variant/30 pt-3">
            <button
              type="button"
              onClick={enterAi}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-brand-600 transition hover:bg-brand-50"
            >
              <Wand2 className="h-3.5 w-3.5" /> AI 改写
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={exitEdit} disabled={saveMut.isPending}>
                <X className="h-3.5 w-3.5" /> 取消
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => saveMut.mutate()}
                disabled={
                  saveMut.isPending ||
                  draft.trim().length === 0 ||
                  draft.trim() === (initial ?? '').trim()
                }
              >
                <Save className="h-3.5 w-3.5" />
                {saveMut.isPending ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ───────── AI 改写态 ─────────
  return (
    <div className="space-y-2 rounded-lg border border-brand-200/60 bg-brand-50/30 p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-brand-700">AI 改写</div>
        <span className={`text-xs ${lengthTone(draft.length)}`}>
          {draft.length} 字
        </span>
      </div>

      <textarea
        ref={textareaRef}
        rows={6}
        value={draft}
        onChange={(e) => !streaming && setDraft(e.target.value)}
        readOnly={streaming}
        placeholder="输入改写要求后点击「AI 改写」，结果将在此实时显示。"
        className="w-full resize-y rounded-lg border border-outline-variant/40 bg-surface px-3 py-2 text-sm leading-relaxed text-neutral-800 placeholder:text-neutral-400 focus:border-brand-400 focus:outline-none disabled:bg-surface-variant/30"
      />

      {streaming && (
        <div className="flex items-center gap-2 rounded-md bg-brand-100/50 px-2.5 py-1 text-xs text-brand-700">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
          </span>
          AI 改写中… 已生成 {draft.length} 字
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          ref={promptRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !streaming && startGenerate()}
          placeholder="改写要求：比如改用甜虐基调 / 加重女主反转 / 突出反派狠辣"
          disabled={streaming}
          className="min-w-[16rem] flex-1"
        />
        {streaming ? (
          <Button variant="danger" size="sm" onClick={abortGenerate}>
            <Square className="h-3.5 w-3.5" /> 停止
          </Button>
        ) : (
          <Button variant="agent" agentColor="writer" size="sm" onClick={startGenerate}>
            <Sparkles className="h-3.5 w-3.5" /> AI 改写
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={exitEdit} disabled={streaming}>
          <X className="h-3.5 w-3.5" /> 取消
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => saveMut.mutate()}
          disabled={streaming || saveMut.isPending || draft.trim().length === 0 || draft.trim() === (initial ?? '').trim()}
        >
          <Save className="h-3.5 w-3.5" />
          {saveMut.isPending ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  );
}
